import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { IconBraces } from "@tabler/icons-react";
import { Popover, PopoverContent, PopoverTrigger, Text } from "@/components/ui";
import { catalogoNotificacionesQueryOptions } from "../../../hooks/use-notificaciones";
import type { VariableDeEvento } from "../../../services/notificaciones-service";
import { EditorPlantilla, type EditorPlantillaHandle, type LenguajePlantilla } from "./EditorPlantilla";

interface Props {
  etiqueta: string;
  valor: string;
  onCambio: (v: string) => void;
  variables: VariableDeEvento[];
  multilinea?: boolean;
  filas?: number;
  /** Resaltado de sintaxis del campo (HTML del correo, JSON del webhook o texto plano). */
  lenguaje?: LenguajePlantilla;
  placeholder?: string;
  ayuda?: string;
  /** Obsoleto: el editor ya usa fuente monoespaciada. Se conserva por compatibilidad de llamadas. */
  mono?: boolean;
}

/**
 * Campo de plantilla con variables <c>{{Var}}</c> sobre un editor tipo Scintilla (CodeMirror):
 * autocompletado al teclear <c>{{</c>, formatos tras <c>|</c>, resaltado y validación en vivo. El
 * botón "Variables" lista todas las del evento para insertarlas en el cursor.
 */
export function CampoPlantilla({ etiqueta, valor, onCambio, variables, multilinea, filas = 4, lenguaje, placeholder, ayuda }: Props) {
  const editor = useRef<EditorPlantillaHandle>(null);
  const { data: catalogo } = useQuery(catalogoNotificacionesQueryOptions);
  const formatos = catalogo?.formatos ?? [];

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="text-xs font-medium">{etiqueta}</label>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-multiva-600 hover:bg-muted" disabled={variables.length === 0}>
              <IconBraces size={12} /> Variables
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="max-h-72 w-72 overflow-y-auto p-1">
            {variables.length === 0 ? (
              <Text size="xs" c="dimmed" className="p-2">Conecta un evento para ver sus variables.</Text>
            ) : (
              variables.map((v) => (
                <button
                  key={v.nombre}
                  type="button"
                  onClick={() => editor.current?.insertarVariable(v.nombre)}
                  className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-xs hover:bg-muted"
                  title={v.descripcion ?? undefined}
                >
                  <span className="font-mono">{v.nombre}</span>
                  <span className="text-muted-foreground">{v.tipo}</span>
                </button>
              ))
            )}
          </PopoverContent>
        </Popover>
      </div>
      <EditorPlantilla
        ref={editor}
        valor={valor}
        onCambio={onCambio}
        variables={variables}
        formatos={formatos}
        lenguaje={lenguaje}
        multilinea={multilinea}
        filas={filas}
        placeholder={placeholder}
        etiquetaAccesible={etiqueta}
      />
      {ayuda ? <Text size="xs" c="dimmed" className="mt-1">{ayuda}</Text> : null}
    </div>
  );
}
